import React, { useState, useEffect, useCallback } from "react";
import { Alert, Table } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableFooter from "../../../components/TableFooter.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { formatLocalDate } from "../../../utils/utils.js";
import {
  isLoadingSoftwarePackages,
  selectSoftwarePackages,
} from "../selectors.js";
import { fetchSoftwarePackages } from "../thunks.js";

const SoftwarePackages = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const pageSize = 15;

  const softwarePackages = useSelector(selectSoftwarePackages);
  const isLoading = useSelector(isLoadingSoftwarePackages);

  const fetchData = useCallback(async () => {
    try {
      const pagesFromResponse = await dispatch(
        fetchSoftwarePackages({ page, pageSize }),
      );
      setPages(pagesFromResponse);
    } catch (err) {
      setError(err);
      setPage(1);
      setPages(0);
    }
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSelectPage = (newPage) => {
    setPage(newPage);
  };

  const mapPackageToTableRow = (softwarePackage) => (
    <tr key={`package-${softwarePackage.id}`}>
      <td>
        <Link to={`/software-package/package/edit/${softwarePackage.id}`}>
          {softwarePackage.code}
        </Link>
      </td>
      <td>{softwarePackage.title}</td>
      <td>{softwarePackage.tag}</td>
      <td>{softwarePackage.information}</td>
      <td>{formatLocalDate(softwarePackage.updatedAt)}</td>
    </tr>
  );

  const dismissError = () => setError(null);

  return (
    <div>
      {error && (
        <Alert variant="danger" onClose={dismissError} dismissible>
          <Alert.Heading>
            {intl.formatMessage(genericTranslations.errorMessage)}
          </Alert.Heading>
          <p>{error.toString()}</p>
        </Alert>
      )}

      {isLoading && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.loadingInProgress)}
        </span>
      )}

      {!isLoading && softwarePackages.length === 0 && (
        <span className="align-middle">
          {intl.formatMessage(genericTranslations.nothingFound)}
        </span>
      )}

      {softwarePackages.length > 0 && !isLoading && (
        <Table responsive>
          <thead>
            <tr>
              <td>CI-nr</td>
              <td>{intl.formatMessage(genericTranslations.title)}</td>
              <td>Tag</td>
              <td>{intl.formatMessage(genericTranslations.information)}</td>
              <td>{intl.formatMessage(genericTranslations.updatedAt)}</td>
            </tr>
          </thead>
          <tbody>{softwarePackages.map(mapPackageToTableRow)}</tbody>
        </Table>
      )}

      <TableFooter
        actionPath="/software-package/package/add"
        onSelectPage={onSelectPage}
        page={page}
        pages={pages}
      />
    </div>
  );
};

export default SoftwarePackages;
